import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { formatMoney, formatQuantity, sum, toDecimal } from '@erp/shared';
import { updsApi, type UpdLine } from '../api/upd';

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** Незаполненное поле бланка — два дефиса, как в типографской форме. */
const EMPTY = '--';

/** «09 ноября 2025 г.» — формат даты в шапке бланка, день с ведущим нулём. */
function formatDateLong(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${MONTHS_GENITIVE[date.getMonth()]} ${date.getFullYear()} г.`;
}

/** «05.08.2026» — короткий формат для имени файла и заголовка листа-продолжения. */
function formatDateShort(value: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** «без НДС» → «Без НДС» — код ставки хранится строчными, в форме печатается с заглавной. */
function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** «Шилов Сергей Александрович» → «Шилов С. А.» — расшифровка подписи в бланке сокращённая. */
function abbreviateName(value: string | null): string {
  if (!value) return '';
  const [last, ...rest] = value.trim().split(/\s+/);
  if (!last || rest.length === 0) return value.trim();
  return `${last} ${rest.map((part) => `${part.charAt(0).toUpperCase()}.`).join(' ')}`;
}

/** «на 1 листе», «на 2 листах» — склонение в строке «Документ составлен на …». */
function sheetsWord(count: number): string {
  const lastTwo = count % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'листах';
  return count % 10 === 1 ? 'листе' : 'листах';
}

/**
 * Разбивка строк по листам. На первом листе помещается меньше — там ещё шапка
 * счёта-фактуры; лист с подвалом (подписи + передаточный акт) тоже вмещает
 * меньше строк. Лимиты заданы числом строк, а не измерением высоты: содержимое
 * ячеек однострочное, и подсчёт по строкам даёт стабильный результат.
 */
const FIRST_PAGE_MAX_LINES = 22;
const CONTINUATION_PAGE_MAX_LINES = 36;
const FOOTER_ALLOWANCE_LINES = 12;

function paginateLines(lines: UpdLine[]): UpdLine[][] {
  if (lines.length === 0) return [[]];
  const pages: UpdLine[][] = [];
  let rest = lines;
  let isFirst = true;

  while (rest.length > 0) {
    const base = isFirst ? FIRST_PAGE_MAX_LINES : CONTINUATION_PAGE_MAX_LINES;
    // Лист становится последним, только если остаток влезает вместе с подвалом.
    // Иначе одна строка намеренно переносится дальше: иначе подписи и блок
    // передаточного акта уехали бы за край листа.
    const fitsWithFooter = rest.length <= base - FOOTER_ALLOWANCE_LINES;
    const take = fitsWithFooter ? rest.length : Math.min(base, rest.length - 1);
    pages.push(rest.slice(0, take));
    rest = rest.slice(take);
    isFirst = false;
  }
  return pages;
}

function sumField(lines: UpdLine[], field: 'amount' | 'vatAmount' | 'totalAmount') {
  return sum(lines.map((l) => toDecimal(l[field])));
}

/**
 * PDF собирается тем же способом, что и у счёта — «скриншот» отрисованного
 * бланка (html2canvas → jsPDF). Страниц может быть несколько, и каждая
 * переснимается по отдельности: так разрывы в PDF совпадают с разрывами,
 * которые задаёт вёрстка (повтор шапки таблицы, итог на каждом листе),
 * а не режут лист по случайной высоте.
 */
async function downloadUpdPdf(pageElements: HTMLElement[], filename: string): Promise<void> {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();

  for (let i = 0; i < pageElements.length; i += 1) {
    const canvas = await html2canvas(pageElements[i]!, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      ignoreElements: (el) => el.classList.contains('no-print'),
    });
    const imgData = canvas.toDataURL('image/png');
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight);
  }

  pdf.save(filename);
}

/** Строка шапки: подпись, подчёркнутое значение, номер поля справа. */
function HeadRow({ label, value, no }: { label: string; value?: string; no: string }) {
  return (
    <>
      <td className="l">{label}</td>
      <td className="v">{value || ''}</td>
      <td className="n">{no}</td>
    </>
  );
}

/**
 * Линия подписи с расшифровкой под ней. Ширины слотов заданы долями, а не
 * содержимым: в бланке линии «должность / подпись / ф.и.о.» стоят на одних
 * местах во всех блоках акта, независимо от длины должности и фамилии.
 */
function SignLine({ position, name, no }: { position?: string; name?: string; no: string }) {
  return (
    <div className="sign">
      <div className="sign-row">
        <span className="u s-pos">{position || ''}</span>
        <span className="u s-sig" />
        <span className="u s-fio">{name || ''}</span>
        <span className="no">{no}</span>
      </div>
      <div className="sign-row caps">
        <span className="s-pos">(должность)</span>
        <span className="s-sig">(подпись)</span>
        <span className="s-fio">(ф.и.о.)</span>
        <span className="no" />
      </div>
    </div>
  );
}

/** Поле акта: заголовок, линия во всю ширину колонки с номером и пояснение под ней. */
function ActField({ title, value, no, caption }: { title: string; value?: string; no: string; caption: string }) {
  return (
    <>
      <div className="act-title">{title}</div>
      <div className="act-line">
        <span className="u fill">{value || ''}</span>
        <span className="no">{no}</span>
      </div>
      <div className="cap">{caption}</div>
    </>
  );
}

/** Заголовок таблицы товаров — повторяется на каждом листе, как в бланке. */
function ItemsHeader() {
  return (
    <thead>
      <tr>
        <th rowSpan={2}>Код товара/ работ, услуг</th>
        <th rowSpan={2}>
          №<br />п/п
        </th>
        <th rowSpan={2}>
          Наименование товара (описание выполненных работ, оказанных услуг), имущественного права
        </th>
        <th rowSpan={2}>
          Код вида
          <br />
          товара
        </th>
        <th colSpan={2}>
          Единица
          <br />
          измерения
        </th>
        <th rowSpan={2}>
          Коли&shy;чество (объем)
        </th>
        <th rowSpan={2}>
          Цена (тариф)
          <br />
          за
          <br />
          единицу измерения
        </th>
        <th rowSpan={2}>
          Стоимость товаров (работ, услуг), имущест&shy;венных прав без налога - всего
        </th>
        <th rowSpan={2}>
          В том
          <br />
          числе
          <br />
          сумма акциза
        </th>
        <th rowSpan={2}>Налоговая ставка</th>
        <th rowSpan={2}>
          Сумма налога, предъяв&shy;ляемая покупателю
        </th>
        <th rowSpan={2}>
          Стоимость товаров (работ, услуг), имущест&shy;венных прав с налогом - всего
        </th>
        <th colSpan={2}>
          Страна
          <br />
          происхождения товара
        </th>
        <th rowSpan={2}>
          Регистрационный номер декларации на товары или регистрационный номер партии товара, подлежащего
          прослеживаемости
        </th>
      </tr>
      <tr>
        <th>код</th>
        <th>
          условное обозна&shy;чение (нацио&shy;нальное)
        </th>
        <th>
          цифро&shy;вой код
        </th>
        <th>
          краткое наимено&shy;вание
        </th>
      </tr>
      <tr className="idx">
        {['А', '1', '1а', '1б', '2', '2а', '3', '4', '5', '6', '7', '8', '9', '10', '10а', '11'].map((n) => (
          <th key={n}>{n}</th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * Печатная форма УПД по приложению № 1 к постановлению Правительства РФ
 * № 1137 в редакции от 23.01.2026 № 26 — счёт-фактура и передаточный
 * документ (акт) в одном бланке.
 *
 * Вёрстка снята с типографского бланка: шапка и подвал собраны таблицами с
 * фиксированными долями колонок. Это не стилистическое решение — только так
 * подчёркивания полей и линии подписей стоят на одних и тех же вертикалях во
 * всех строках; при вёрстке «подпись + линия на остаток строки» каждая строка
 * начинается со своего места и бланк перестаёт совпадать с формой.
 *
 * Ориентация альбомная: в таблице товаров 16 граф, в книжной A4 они
 * нечитаемы. Отдельный маршрут без AppShell, как и печать счёта: бланк
 * должен занимать весь лист. window.print() вызывается автоматически при
 * обычном открытии.
 */
export function UpdPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const autoDownload = searchParams.get('download') === '1';
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const updQuery = useQuery({
    queryKey: ['upd', id],
    queryFn: () => updsApi.get(id!),
    enabled: !!id,
  });

  const upd = updQuery.data?.upd;

  function pdfFilename(): string {
    return `УПД № ${upd!.number} от ${formatDateShort(upd!.date)}.pdf`;
  }

  async function handleDownload() {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      const pageElements = Array.from(printRef.current.querySelectorAll<HTMLElement>('.upd-page'));
      await downloadUpdPdf(pageElements, pdfFilename());
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    if (!upd) return;
    const timer = setTimeout(() => {
      if (autoDownload) void handleDownload();
      else window.print();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upd, autoDownload]);

  if (!upd) {
    return <div style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>Загрузка…</div>;
  }

  const org = upd.organization;
  const cp = upd.counterparty;
  const isEntrepreneur = org.kind === 'ENTREPRENEUR';

  // Строки 3 и 4: если грузоотправитель совпадает с продавцом, бланк требует
  // именно «он же», а не повтор реквизитов.
  const shipperText = upd.shipperIsSeller
    ? 'он же'
    : [upd.shipperName, upd.shipperAddress].filter(Boolean).join(', ') || EMPTY;
  const consigneeText = upd.consigneeIsBuyer
    ? [cp.fullName, cp.actualAddressFull ?? cp.addressFull].filter(Boolean).join(', ')
    : [upd.consigneeName, upd.consigneeAddress].filter(Boolean).join(', ') || EMPTY;

  // Создание УПД для организации без ИНН заблокировано на бэкенде — это
  // defensive-only ветка для типобезопасности и уже существующих старых документов.
  const sellerInnKpp = org.inn ? (org.kpp ? `${org.inn} / ${org.kpp}` : org.inn) : '—';
  const buyerInnKpp = cp.inn ? (cp.kpp ? `${cp.inn} / ${cp.kpp}` : cp.inn) : '';

  // «Универсальный передаточный документ, №87 от 09 ноября 2025 г.» — запятая
  // только после названия, номер и дата идут одной группой.
  const shipmentDoc = [
    upd.shipmentDocName,
    [
      upd.shipmentDocNumber ? `№${upd.shipmentDocNumber}` : null,
      upd.shipmentDocDate ? `от ${formatDateLong(upd.shipmentDocDate)}` : null,
    ]
      .filter(Boolean)
      .join(' ') || null,
  ]
    .filter(Boolean)
    .join(', ');

  // «Без НДС» — не то же самое, что 0%: операция не облагается, и вместо суммы
  // налога в бланке печатается прочерк, а не ноль.
  const vatExempt = upd.lines.every((l) => l.vatRateValue === null);
  const signerName = abbreviateName(upd.signerName);
  const signerPosition = upd.signerPosition ?? '';
  // С 2026 года свидетельство о госрегистрации ИП отменено — бланк требует
  // ОГРНИП и дату его присвоения.
  const registrationInfo = org.ogrn
    ? `${org.ogrn}${org.ogrnipDate ? ` от ${formatDateShort(org.ogrnipDate)}` : ''}`
    : '';

  const pages = paginateLines(upd.lines);

  return (
    <div className="upd-print" ref={printRef}>
      <style>{`
        @page { size: A4 landscape; margin: 0; }
        .upd-print { font-family: Arial, Helvetica, sans-serif; font-size: 8px; color: #000; line-height: 1.15; }
        .upd-print .upd-page {
          box-sizing: border-box;
          width: 297mm;
          min-height: 210mm;
          margin: 0 auto 16px;
          padding: 5mm;
          background: #fff;
        }

        /* Общий примитив бланка: подчёркнутая строка для рукописного или
           печатного значения. */
        .upd-print .u { border-bottom: 1px solid #000; padding: 0 3px; min-height: 11px; }
        .upd-print .u:empty::after { content: '\\00a0'; }
        .upd-print .no { font-size: 6.5px; text-align: right; white-space: nowrap; }
        .upd-print .cap { display: block; font-size: 6px; text-align: center; line-height: 1.2; }

        /* ── Шапка счёта-фактуры ──────────────────────────────────────────
           Таблица, а не набор строк: доли колонок фиксированы, поэтому
           подчёркивания всех полей начинаются от одной вертикали, а номера
           полей стоят у правого края своей колонки. */
        .upd-print .hdr { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .upd-print .hdr td { vertical-align: bottom; height: 13px; padding: 1px 0 0; }
        .upd-print .hdr td.l { padding-right: 4px; }
        .upd-print .hdr td.v { border-bottom: 1px solid #000; padding-left: 4px; }
        /* Отступ справа обязателен: без него номер поля левого блока
           упирается в подпись правого — «(2)Покупатель:». */
        .upd-print .hdr td.n { font-size: 6.5px; text-align: right; padding-right: 10px; }
        .upd-print .hdr td.side { vertical-align: top; padding-right: 6px; }
        .upd-print .hdr td.title { line-height: 1.25; }
        .upd-print .hdr td.status { white-space: nowrap; vertical-align: bottom; padding-bottom: 1px; }
        .upd-print .hdr td.status b { border: 1px solid #000; padding: 0 5px; font-weight: 400; margin-left: 3px; }
        .upd-print .hdr td.legend { font-size: 6px; line-height: 1.3; }
        .upd-print .hdr td.legal { text-align: right; font-size: 6.5px; line-height: 1.3; vertical-align: top; }
        .upd-print .hdr .split { display: flex; align-items: flex-end; gap: 5px; }
        .upd-print .hdr .split .u { flex: 1 1 0; text-align: center; }
        .upd-print .hdr .split .u.wide { flex: 1.3 1 0; }
        /* Поле 5б: подпись длиннее любой колонки, поэтому занимает всю ширину
           блока продавца и переносится, а слоты «№ … от …» стоят строкой ниже. */
        .upd-print .hdr td.l5b { white-space: normal; line-height: 1.25; padding-right: 4px; }
        .upd-print .hdr .split5b { display: flex; align-items: flex-end; gap: 4px; }
        .upd-print .hdr .split5b .u { flex: 0 1 70px; }
        .upd-print .hdr .split5b .tail { flex: 1 1 auto; }

        /* ── Таблица товаров ───────────────────────────────────────────── */
        .upd-print .items { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 5px; }
        .upd-print .items th, .upd-print .items td {
          border: 1px solid #000; padding: 1px 3px; font-size: 7px;
        }
        /* Заголовки переносятся только по мягким переносам, расставленным в
           разметке: разрыв в произвольном месте даёт «обозначени|е». Ломать
           слова разрешено лишь в данных — там встречаются длинные артикулы. */
        .upd-print .items th {
          font-weight: 400; text-align: center; vertical-align: middle; line-height: 1.1; hyphens: manual;
        }
        .upd-print .items .idx th { font-size: 6.5px; }
        .upd-print .items td { vertical-align: top; overflow-wrap: anywhere; }
        .upd-print .items td.r { text-align: right; }
        .upd-print .items tfoot td { font-weight: 700; }
        .upd-print .items tfoot td.c { text-align: center; }

        /* ── Подписи счёта-фактуры ─────────────────────────────────────── */
        .upd-print .sigs { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 6px; }
        .upd-print .sigs td { vertical-align: bottom; padding: 3px 4px 0 0; }
        .upd-print .sigs td.sheets { vertical-align: top; line-height: 1.3; padding-top: 4px; }
        .upd-print .sigs .role { line-height: 1.25; }
        .upd-print .sigs .u { display: block; }

        /* ── Передаточный документ (акт) ───────────────────────────────── */
        .upd-print .act { margin-top: 6px; }
        .upd-print .act-row { display: flex; align-items: flex-end; gap: 5px; }
        .upd-print .act-row .u { flex: 1 1 0; }
        .upd-print .act-row .no { flex: 0 0 20px; }
        .upd-print .act-cols { display: flex; margin-top: 6px; }
        .upd-print .act-col { flex: 1 1 0; min-width: 0; padding-right: 12px; }
        .upd-print .act-col + .act-col { border-left: 1px solid #000; padding-left: 12px; padding-right: 0; }
        .upd-print .act-title { margin-top: 4px; }
        .upd-print .act-line { display: flex; align-items: flex-end; gap: 5px; margin-top: 8px; }
        .upd-print .act-line .u.fill { flex: 1 1 0; }
        .upd-print .act-line .no { flex: 0 0 20px; }
        /* Дата отгрузки: в бланке линия не тянется до номера поля, между ними
           остаётся заметный отступ. */
        .upd-print .act-date { display: flex; align-items: flex-end; gap: 5px; margin-top: 4px; }
        .upd-print .act-date .lbl { flex: 0 0 auto; }
        .upd-print .act-date .u { flex: 1 1 0; }
        .upd-print .act-date .tail { flex: 0 0 26%; }
        .upd-print .act-date .no { flex: 0 0 20px; }

        .upd-print .sign { margin-top: 2px; }
        .upd-print .sign-row { display: flex; align-items: flex-end; gap: 6px; }
        .upd-print .sign-row.caps { font-size: 6px; text-align: center; }
        .upd-print .sign-row .s-pos { flex: 0 0 29%; }
        .upd-print .sign-row .s-sig { flex: 0 0 29%; }
        .upd-print .sign-row .s-fio { flex: 1 1 0; }
        .upd-print .sign-row .no { flex: 0 0 20px; }
        .upd-print .stamp { margin-top: 5px; }

        .upd-print .cont {
          display: flex; justify-content: space-between; align-items: baseline;
          font-weight: 700; font-size: 9px; border-bottom: 1px solid #000;
          padding-bottom: 3px; margin-bottom: 4px;
        }

        @media screen {
          .upd-print { background: #ddd; padding: 16px 0; }
          .upd-print .upd-page { box-shadow: 0 0 8px rgba(0,0,0,0.2); }
        }
        @media print {
          .no-print { display: none !important; }
          .upd-print { background: #fff; padding: 0; }
          .upd-print .upd-page { min-height: 0; margin: 0; box-shadow: none; page-break-after: always; }
          .upd-print .upd-page:last-child { page-break-after: auto; }
        }
      `}</style>

      <div
        className="no-print"
        style={{ textAlign: 'center', marginBottom: 12, display: 'flex', gap: 8, justifyContent: 'center' }}
      >
        <button onClick={() => window.print()} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          Печать
        </button>
        <button onClick={() => void handleDownload()} disabled={downloading} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          {downloading ? 'Формирование PDF…' : 'Скачать PDF'}
        </button>
      </div>

      {pages.map((pageLines, pageIndex) => {
        const isFirst = pageIndex === 0;
        const isLast = pageIndex === pages.length - 1;
        const pageAmount = sumField(pageLines, 'amount');
        const pageVat = sumField(pageLines, 'vatAmount');
        const pageTotal = sumField(pageLines, 'totalAmount');

        return (
          <div className="upd-page" key={pageIndex}>
            {isFirst ? (
              <table className="hdr">
                <colgroup>
                  <col style={{ width: '8.5%' }} />
                  <col style={{ width: '15.5%' }} />
                  <col style={{ width: '29%' }} />
                  <col style={{ width: '2.5%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '2.5%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td className="side title" rowSpan={3}>
                      Универсальный
                      <br />
                      передаточный
                      <br />
                      документ
                    </td>
                    <td className="l">Счет-фактура №</td>
                    <td>
                      <div className="split">
                        <span className="u">{upd.number}</span>
                        <span>от</span>
                        <span className="u wide">{formatDateLong(upd.date)}</span>
                      </div>
                    </td>
                    <td className="n">(1)</td>
                    {/* Ссылка на постановление стоит в бланке справа вверху, на
                        уровне первых двух полей, а не отдельной строкой. */}
                    <td className="legal" colSpan={3} rowSpan={2}>
                      Приложение № 1 к постановлению Правительства Российской Федерации от 26 декабря 2011 г. № 1137
                      <br />
                      (в редакции постановления Правительства Российской Федерации от 23 января 2026 г. № 26)
                    </td>
                  </tr>
                  <tr>
                    <td className="l">Исправление №</td>
                    <td>
                      <div className="split">
                        <span className="u">{EMPTY}</span>
                        <span>от</span>
                        <span className="u wide">{EMPTY}</span>
                      </div>
                    </td>
                    <td className="n">(1а)</td>
                  </tr>
                  <tr>
                    <HeadRow label="Продавец:" value={org.fullName} no="(2)" />
                    <HeadRow label="Покупатель:" value={cp.fullName} no="(6)" />
                  </tr>
                  <tr>
                    <td className="side status">
                      Статус:<b>{upd.documentStatus}</b>
                    </td>
                    <HeadRow label="Адрес:" value={org.addressFull ?? EMPTY} no="(2а)" />
                    <HeadRow label="Адрес:" value={cp.addressFull ?? EMPTY} no="(6а)" />
                  </tr>
                  <tr>
                    <td className="side legend" rowSpan={7}>
                      1 – счет-фактура и передаточный документ (акт)
                      <br />
                      <br />
                      2 – передаточный документ (акт)
                    </td>
                    <HeadRow label="ИНН/КПП продавца:" value={sellerInnKpp} no="(2б)" />
                    <HeadRow label="ИНН/КПП покупателя:" value={buyerInnKpp} no="(6б)" />
                  </tr>
                  <tr>
                    <HeadRow label="Грузоотправитель и его адрес:" value={shipperText} no="(3)" />
                    <HeadRow
                      label="Валюта: наименование, код"
                      value={`Российский рубль, ${upd.currencyCode}`}
                      no="(7)"
                    />
                  </tr>
                  <tr>
                    <HeadRow label="Грузополучатель и его адрес:" value={consigneeText} no="(4)" />
                    <td colSpan={3}>Идентификатор государственного контракта,</td>
                  </tr>
                  <tr>
                    <HeadRow label="К платежно-расчетному документу №" no="(5)" />
                    <HeadRow
                      label="договора (соглашения) (при наличии):"
                      value={upd.governmentContractId ?? ''}
                      no="(8)"
                    />
                  </tr>
                  <tr>
                    <HeadRow label="Документ об отгрузке" value={shipmentDoc} no="(5а)" />
                    <td colSpan={3} />
                  </tr>
                  <tr>
                    <td className="l5b" colSpan={2}>
                      К счету-фактуре (счетам-фактурам), выставленному (выставленным) при получении оплаты, частичной
                      оплаты или иных платежей в счет предстоящих поставок товаров (выполнения работ, оказания услуг),
                      передачи имущественных прав
                    </td>
                    <td />
                    <td colSpan={3} />
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <div className="split5b">
                        <span>№</span>
                        <span className="u" />
                        <span>от</span>
                        <span className="u" />
                        <span>, исправление №</span>
                        <span className="u" />
                        <span>от</span>
                        <span className="u" />
                        <span className="tail" />
                      </div>
                    </td>
                    <td className="n">(5б)</td>
                    <td colSpan={3} />
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="cont">
                <span>
                  Универсальный передаточный документ № {upd.number} от {formatDateShort(upd.date)}
                </span>
                <span>
                  Лист {pageIndex + 1} из {pages.length}
                </span>
              </div>
            )}

            <table className="items">
              <colgroup>
                <col style={{ width: '9.5%' }} />
                <col style={{ width: '2.3%' }} />
                <col style={{ width: '12.8%' }} />
                <col style={{ width: '4.4%' }} />
                <col style={{ width: '2.4%' }} />
                <col style={{ width: '4.7%' }} />
                <col style={{ width: '4.8%' }} />
                <col style={{ width: '5.6%' }} />
                <col style={{ width: '7.6%' }} />
                <col style={{ width: '4%' }} />
                <col style={{ width: '5.3%' }} />
                <col style={{ width: '6.6%' }} />
                <col style={{ width: '7.3%' }} />
                <col style={{ width: '2.4%' }} />
                <col style={{ width: '6.5%' }} />
                <col style={{ width: '13.8%' }} />
              </colgroup>
              <ItemsHeader />
              <tbody>
                {pageLines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.article || EMPTY}</td>
                    <td className="r">{line.lineNumber}</td>
                    <td>{line.name}</td>
                    <td>{line.tnvedCode || EMPTY}</td>
                    <td>{line.unitCode || EMPTY}</td>
                    <td>{line.unitSymbol || EMPTY}</td>
                    <td className="r">{formatQuantity(line.quantity)}</td>
                    <td className="r">{formatMoney(line.price)}</td>
                    <td className="r">{formatMoney(line.amount)}</td>
                    <td>{line.exciseAmount ? formatMoney(line.exciseAmount) : 'Без акциза'}</td>
                    <td>{capitalize(line.vatRateCode)}</td>
                    <td className="r">{line.vatRateValue === null ? EMPTY : formatMoney(line.vatAmount)}</td>
                    <td className="r">{formatMoney(line.totalAmount)}</td>
                    <td>{line.countryCode || EMPTY}</td>
                    <td>{line.countryName || EMPTY}</td>
                    <td>{line.customsDeclarationNumber || EMPTY}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {/* В бланке итоговая строка одна. Постраничный итог появляется
                    только когда товары не поместились на один лист. */}
                {pages.length > 1 && (
                  <tr>
                    <td colSpan={8}>Итого по листу</td>
                    <td className="r">{formatMoney(pageAmount)}</td>
                    <td className="c" colSpan={2}>
                      Х
                    </td>
                    <td className="r">{vatExempt ? EMPTY : formatMoney(pageVat)}</td>
                    <td className="r">{formatMoney(pageTotal)}</td>
                    <td colSpan={3} />
                  </tr>
                )}
                {isLast && (
                  <tr>
                    <td colSpan={8}>Всего к оплате (9)</td>
                    <td className="r">{formatMoney(upd.subtotal)}</td>
                    <td className="c" colSpan={2}>
                      Х
                    </td>
                    <td className="r">{vatExempt ? EMPTY : formatMoney(upd.vatAmount)}</td>
                    <td className="r">{formatMoney(upd.total)}</td>
                    <td colSpan={3} />
                  </tr>
                )}
              </tfoot>
            </table>

            {isLast && (
              <>
                <table className="sigs">
                  <colgroup>
                    <col style={{ width: '8.9%' }} />
                    <col style={{ width: '16.9%' }} />
                    <col style={{ width: '12.1%' }} />
                    <col style={{ width: '14.5%' }} />
                    <col style={{ width: '16.9%' }} />
                    <col style={{ width: '12.1%' }} />
                    <col style={{ width: '18.6%' }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td className="sheets" rowSpan={2}>
                        Документ
                        <br />
                        составлен на
                        <br />
                        {pages.length} {sheetsWord(pages.length)}
                      </td>
                      <td className="role">Руководитель организации или иное уполномоченное лицо</td>
                      <td>
                        <span className="u" />
                        <span className="cap">(подпись)</span>
                      </td>
                      <td>
                        <span className="u">{isEntrepreneur ? '' : abbreviateName(org.directorName)}</span>
                        <span className="cap">(ф.и.о.)</span>
                      </td>
                      <td className="role">Главный бухгалтер или иное уполномоченное лицо</td>
                      <td>
                        <span className="u" />
                        <span className="cap">(подпись)</span>
                      </td>
                      <td>
                        {/* Если обязанности главбуха возложены на руководителя,
                            расшифровка не печатается: линия в бланке остаётся,
                            но отдельного ответственного лица нет. */}
                        <span className="u">
                          {isEntrepreneur || org.accountantIsDirector ? '' : abbreviateName(org.accountantName)}
                        </span>
                        <span className="cap">(ф.и.о.)</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="role">Индивидуальный предприниматель или иное уполномоченное лицо</td>
                      <td>
                        <span className="u" />
                        <span className="cap">(подпись)</span>
                      </td>
                      <td>
                        <span className="u">{isEntrepreneur ? abbreviateName(org.directorName) : ''}</span>
                        <span className="cap">(ф.и.о.)</span>
                      </td>
                      <td colSpan={3}>
                        <span className="u">{isEntrepreneur ? registrationInfo : ''}</span>
                        <span className="cap">
                          (основной государственный регистрационный номер индивидуального предпринимателя и дата
                          присвоения такого номера)
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="act">
                  <div className="act-row" style={{ marginTop: 4 }}>
                    <span style={{ flex: '0 0 auto' }}>Основание передачи (сдачи) / получения (приемки)</span>
                    <span className="u">{upd.transferBasis ?? ''}</span>
                    <span className="no">[8]</span>
                  </div>
                  <div className="cap">(договор; доверенность и др.)</div>

                  <div className="act-row" style={{ marginTop: 3 }}>
                    <span style={{ flex: '0 0 auto' }}>Данные о транспортировке и грузе</span>
                    <span className="u" />
                    <span className="no">[9]</span>
                  </div>
                  <div className="cap">
                    (транспортная накладная, поручение экспедитору, экспедиторская / складская расписка и др. / масса
                    нетто/ брутто груза, если не приведены ссылки на транспортные документы, содержащие эти сведения)
                  </div>

                  <div className="act-cols">
                    <div className="act-col">
                      <div>Товар (груз) передал / услуги, результаты работ, права сдал</div>
                      <SignLine position={signerPosition} name={signerName} no="[10]" />

                      <div className="act-date">
                        <span className="lbl">Дата отгрузки, передачи (сдачи)</span>
                        <span className="u">{formatDateLong(upd.transferDate)}</span>
                        <span className="tail" />
                        <span className="no">[11]</span>
                      </div>

                      <ActField
                        title="Иные сведения об отгрузке, передаче"
                        no="[12]"
                        caption="(ссылки на неотъемлемые приложения, сопутствующие документы, иные документы и т.п.)"
                      />

                      <div className="act-title">
                        Ответственный за правильность оформления факта хозяйственной жизни
                      </div>
                      <SignLine position={signerPosition} name={signerName} no="[13]" />

                      <ActField
                        title="Наименование экономического субъекта – составителя документа (в т.ч. комиссионера / агента)"
                        value={org.inn ? `${org.fullName}, ИНН ${org.inn}` : org.fullName}
                        no="[14]"
                        caption="(может не заполняться при проставлении печати в М.П., может быть указан ИНН / КПП)"
                      />
                      <div className="stamp">М.П.</div>
                    </div>

                    <div className="act-col">
                      <div>Товар (груз) получил / услуги, результаты работ, права принял</div>
                      <SignLine no="[15]" />

                      <div className="act-date">
                        <span className="lbl">Дата получения (приемки)</span>
                        <span className="u" />
                        <span className="tail" />
                        <span className="no">[16]</span>
                      </div>

                      <ActField
                        title="Иные сведения о получении, приемке"
                        no="[17]"
                        caption="(информация о наличии/отсутствии претензии; ссылки на неотъемлемые приложения, и другие документы и т.п.)"
                      />

                      <div className="act-title">
                        Ответственный за правильность оформления факта хозяйственной жизни
                      </div>
                      <SignLine no="[18]" />

                      <ActField
                        title="Наименование экономического субъекта – составителя документа"
                        value={cp.inn ? `${cp.fullName}, ИНН ${cp.inn}` : cp.fullName}
                        no="[19]"
                        caption="(может не заполняться при проставлении печати в М.П., может быть указан ИНН / КПП)"
                      />
                      <div className="stamp">М.П.</div>
                    </div>
                  </div>
                </div>

                {upd.comment && <div style={{ marginTop: 6, whiteSpace: 'pre-line' }}>{upd.comment}</div>}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
